import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Navigate } from "react-router";
import { ArrowLeft, ArrowRight, CalendarCheck, Plus } from "lucide-react";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@runcheetah/api/router";
import { toast } from "sonner";

import { CenteredLoader } from "@/components/common";
import { ScheduledPostPreviewOverlay } from "@/components/campaign/scheduled-post-preview-overlay";
import { CalendarPostGrid } from "@/components/calendar/calendar-post-grid";
import { CreateAssetBatchModal } from "@/components/standalone-assets/create-asset-batch-modal";
import { getUnavailableBrandPath, useRouteBrand } from "@/hooks/use-route-brand";
import { useAssetCreationGate } from "@/hooks/use-asset-creation-gate";
import {
  addCalendarDays,
  getCalendarEventRange,
  getCalendarRange,
  getStandaloneCalendarGroupLabel,
  getCalendarWindowDays,
  getCalendarWindowStart,
  groupCalendarEventsByDay,
  groupCalendarPostsByDay,
  isCalendarPostVisible,
} from "@/lib/calendar-posts";
import { formatInstagramProfileLabel } from "@/lib/social-post-chrome";
import { getStandaloneAssetFirstScheduledForOnDate } from "@/lib/standalone-assets";
import { trpc } from "@/lib/trpc";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@runcheetah/ui";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type CalendarPost = RouterOutputs["calendar"]["list"][number];
type CalendarFilter = "all" | "scheduled" | "needs-approvals";

const CALENDAR_APPROVAL_FILTERS: Record<
  CalendarFilter,
  Array<CalendarPost["approvalStatus"]> | undefined
> = {
  all: undefined,
  scheduled: ["approved"],
  "needs-approvals": ["pending"],
};

const scheduledToastFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

export default function CalendarPage() {
  const intl = useIntl();
  const [windowStart, setWindowStart] = useState(() => getCalendarWindowStart(new Date()));
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createInitialScheduledFor, setCreateInitialScheduledFor] = useState<string | undefined>();
  const [filter, setFilter] = useState<CalendarFilter>("scheduled");
  const { brand, brandQuery } = useRouteBrand();
  const assetCreationGate = useAssetCreationGate(brand?.id);
  const utils = trpc.useUtils();
  const days = useMemo(() => getCalendarWindowDays(windowStart), [windowStart]);
  const range = useMemo(() => getCalendarRange(windowStart), [windowStart]);
  const eventRange = useMemo(() => getCalendarEventRange(windowStart), [windowStart]);
  const calendarQuery = trpc.calendar.list.useQuery(
    {
      brandId: brand?.id ?? "",
      start: range.start,
      end: range.end,
      approvalStatuses: CALENDAR_APPROVAL_FILTERS[filter],
    },
    { enabled: Boolean(brand?.id), refetchOnWindowFocus: true },
  );
  const calendarEventsQuery = trpc.calendar.events.useQuery(
    { brandId: brand?.id ?? "", ...eventRange },
    { enabled: Boolean(brand?.id), refetchOnWindowFocus: true },
  );
  const strategyQuery = trpc.strategy.getMine.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), refetchOnWindowFocus: true },
  );
  const logosQuery = trpc.brand.listLogos.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), staleTime: 0 },
  );
  const logoAssets = useMemo(
    () =>
      (logosQuery.data?.logos ?? []).map((logo) => ({
        objectKey: logo.objectKey,
        signedUrl: logo.signedUrl,
        isPrimary: logo.isPrimary,
      })),
    [logosQuery.data?.logos],
  );
  const approveMutation = trpc.calendar.approve.useMutation({
    onSuccess: async () => {
      await utils.calendar.list.invalidate();
    },
  });
  const rejectMutation = trpc.calendar.reject.useMutation({
    onSuccess: async () => {
      await utils.calendar.list.invalidate();
    },
  });

  if (brandQuery.isPending) {
    return (
      <CenteredLoader
        label={intl.formatMessage({
          id: "calendar.loading",
          defaultMessage: "Loading calendar…",
        })}
      />
    );
  }

  if (!brand || brand.status !== "scanned") {
    return <Navigate to={getUnavailableBrandPath(brand?.id)} replace />;
  }

  const visiblePosts = (calendarQuery.data ?? []).filter(isCalendarPostVisible);
  const groupedDays = groupCalendarPostsByDay(days, visiblePosts, {
    standaloneLabel: getStandaloneCalendarGroupLabel(intl),
  });
  const calendarEventsByDay = groupCalendarEventsByDay(days, calendarEventsQuery.data ?? []);
  const orderedPosts = groupedDays.flatMap((day) => day.groups.flatMap((group) => group.posts));
  const dailyPostCapacity = strategyQuery.data?.postsPerDay ?? null;
  const profileLabel = formatInstagramProfileLabel(brand.sourceDomain);
  const windowEnd = addCalendarDays(windowStart, 6);
  const titleFormatter = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  });
  const selectedPost = selectedPostId
    ? (orderedPosts.find((post) => post.id === selectedPostId) ?? null)
    : null;
  const selectedPostIndex = selectedPost
    ? orderedPosts.findIndex((post) => post.id === selectedPost.id)
    : -1;
  const previousPost = selectedPostIndex > 0 ? orderedPosts[selectedPostIndex - 1] : null;
  const nextPost =
    selectedPostIndex >= 0 && selectedPostIndex < orderedPosts.length - 1
      ? orderedPosts[selectedPostIndex + 1]
      : null;
  const calendarPending = calendarQuery.isPending || calendarEventsQuery.isPending;
  const calendarError = calendarQuery.error ?? calendarEventsQuery.error;
  const openCreate = (initialScheduledFor?: string) => {
    setCreateInitialScheduledFor(initialScheduledFor);
    setCreateOpen(true);
  };

  return (
    <div className="min-w-0 space-y-6 py-6">
      <header className="flex flex-col gap-4 border-b px-4 pb-5 sm:px-6 lg:px-8 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex flex-wrap items-center gap-4">
          <h1 className="text-2xl font-semibold tracking-tight">
            <FormattedMessage id="calendar.title" defaultMessage="Calendar" />
          </h1>
          <div className="flex items-center gap-1 text-sm font-semibold">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setWindowStart((current) => addCalendarDays(current, -7))}
              aria-label={intl.formatMessage({
                id: "calendar.previousRange",
                defaultMessage: "Previous 7 days",
              })}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <div className="min-w-32 text-center">
              {titleFormatter.format(windowStart)} - {titleFormatter.format(windowEnd)}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setWindowStart(getCalendarWindowStart(new Date()))}
            >
              <CalendarCheck className="h-4 w-4" aria-hidden="true" />
              <FormattedMessage id="calendar.today" defaultMessage="Today" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setWindowStart((current) => addCalendarDays(current, 7))}
              aria-label={intl.formatMessage({
                id: "calendar.nextRange",
                defaultMessage: "Next 7 days",
              })}
            >
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/60">
              <FormattedMessage id="calendar.filter.show" defaultMessage="Show" />
            </span>
            <Select value={filter} onValueChange={(value) => setFilter(value as CalendarFilter)}>
              <SelectTrigger
                className="w-[9.5rem] bg-background text-left text-sm text-foreground [&>span]:flex-1 [&>span]:text-left"
                aria-label={intl.formatMessage({
                  id: "calendar.filter.label",
                  defaultMessage: "Filter calendar posts",
                })}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end">
                <SelectItem value="all">
                  <FormattedMessage id="calendar.filter.all" defaultMessage="All" />
                </SelectItem>
                <SelectItem value="scheduled">
                  <FormattedMessage id="calendar.filter.scheduled" defaultMessage="Scheduled" />
                </SelectItem>
                <SelectItem value="needs-approvals">
                  <FormattedMessage
                    id="calendar.filter.needsApprovals"
                    defaultMessage="Need approvals"
                  />
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            variant="brand"
            size="sm"
            className="ml-1"
            onClick={() => assetCreationGate.requestAssetCreation(() => openCreate())}
            disabled={assetCreationGate.gateState === "loading"}
            aria-label={intl.formatMessage({
              id: "posts.createPosts",
              defaultMessage: "Create posts",
            })}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            <FormattedMessage id="common.create" defaultMessage="Create" />
          </Button>
        </div>
      </header>

      <CalendarPostGrid
        groupedDays={groupedDays}
        profileLabel={profileLabel}
        dailyPostCapacity={dailyPostCapacity}
        eventsByDay={calendarEventsByDay}
        loading={calendarPending}
        errorMessage={calendarError?.message ?? null}
        logoAssets={logoAssets}
        showCreateEmptyState={true}
        countScheduledOnly={true}
        onCreateForDate={(date) =>
          assetCreationGate.requestAssetCreation(() =>
            openCreate(getStandaloneAssetFirstScheduledForOnDate(date)),
          )
        }
        onOpenPost={(post) => setSelectedPostId(post.id)}
      />

      <ScheduledPostPreviewOverlay
        post={selectedPost}
        brandId={brand.id}
        profileLabel={profileLabel}
        logoAssets={logoAssets}
        backLabel={intl.formatMessage({
          id: "calendar.title",
          defaultMessage: "Calendar",
        })}
        hasPrevious={Boolean(previousPost)}
        hasNext={Boolean(nextPost)}
        approving={
          approveMutation.isPending &&
          approveMutation.variables?.scheduledPostId === selectedPost?.id
        }
        rejecting={
          rejectMutation.isPending && rejectMutation.variables?.scheduledPostId === selectedPost?.id
        }
        onClose={() => setSelectedPostId(null)}
        onPrevious={previousPost ? () => setSelectedPostId(previousPost.id) : undefined}
        onNext={nextPost ? () => setSelectedPostId(nextPost.id) : undefined}
        onApprove={
          selectedPost
            ? () =>
                approveMutation.mutate(
                  {
                    brandId: brand.id,
                    scheduledPostId: selectedPost.id,
                  },
                  {
                    onSuccess: () => {
                      toast.success(
                        intl.formatMessage(
                          { id: "calendar.scheduledToast", defaultMessage: "Scheduled for {time}" },
                          {
                            time: scheduledToastFormatter.format(
                              new Date(selectedPost.scheduledFor),
                            ),
                          },
                        ),
                      );
                    },
                    onError: (error) => {
                      toast.error(
                        intl.formatMessage(
                          {
                            id: "calendar.approvalFailed",
                            defaultMessage: "Could not approve post: {message}",
                          },
                          { message: error.message },
                        ),
                      );
                    },
                  },
                )
            : undefined
        }
        onReject={
          selectedPost
            ? () =>
                rejectMutation.mutate({
                  brandId: brand.id,
                  scheduledPostId: selectedPost.id,
                })
            : undefined
        }
      />
      <CreateAssetBatchModal
        brandId={brand.id}
        open={createOpen}
        onOpenChange={setCreateOpen}
        initialFirstScheduledFor={createInitialScheduledFor}
        onUpgradeRequired={assetCreationGate.openUpgradeModal}
        upgradeLabel={assetCreationGate.upgradeLabel}
      />
      {assetCreationGate.upgradeModal}
    </div>
  );
}
